/**
 * @fileoverview Indicadores: avisos de conversas sem resposta, as cores do
 * tempo médio de resposta nos relatórios (até o 1º limite verde, até o 2º
 * amarelo, acima vermelho — AverageResponseTimeService no backend) e as
 * respostas curtas do cliente ("Ok", "👍"), que não contam no tempo de resposta
 * nem no "sem resposta" (services/MessageServices/shortReply.ts no backend).
 */

import React, { useEffect, useState } from "react";
import { Collapse, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useDebouncedCallback } from "use-debounce";

import SettingsSection from "../../../components/Settings/SettingsSection";
import SettingItem from "../../../components/Settings/SettingItem";
import { i18n } from "../../../translate/i18n";
import { bindSetting, useDraftSetting } from "../useSettingsArea";
import useSectionStyles from "./sectionStyles";

const YELLOW = "artYellowThresholdMinutes";
const RED = "artRedThresholdMinutes";
const t = (key) => i18n.t(`settings.sections.indicators.${key}`);

/** Erro do par de limites (chave de i18n) ou null se o par pode ser gravado. */
export const validateArtPair = (yellow, red) => {
  const y = Number(yellow);
  const r = Number(red);
  if (!Number.isInteger(y) || !Number.isInteger(r)) return "integer";
  if (y < 1 || y > 1440 || r < 1 || r > 1440) return "range";
  if (y >= r) return "order";
  return null;
};

const ArtThresholds = ({ area }) => {
  const classes = useSectionStyles();
  const [yellow, setYellow] = useState(() => String(area.get(YELLOW, "10")));
  const [red, setRed] = useState(() => String(area.get(RED, "20")));
  const error = validateArtPair(yellow, red);

  // Grava o par quando ele fica válido. Antes, consertar o vermelho depois de
  // um amarelo inválido gravava só o vermelho.
  // Se uma das duas gravações falhar, o campo dela volta ao valor salvo: a tela
  // mostra o par que o servidor tem (e o aviso de par inválido, se for o caso).
  const commit = useDebouncedCallback((nextYellow, nextRed) => {
    if (validateArtPair(nextYellow, nextRed)) return;
    const savedYellow = String(area.get(YELLOW, "10"));
    const savedRed = String(area.get(RED, "20"));
    if (nextYellow !== savedYellow) {
      area.save(YELLOW, nextYellow).then((ok) => ok || setYellow(savedYellow));
    }
    if (nextRed !== savedRed) {
      area.save(RED, nextRed).then((ok) => ok || setRed(savedRed));
    }
  }, 1000);

  useEffect(() => () => commit.flush(), [commit]);

  const change = (setter, which) => (event) => {
    const value = event.target.value;
    setter(value);
    commit(which === "yellow" ? value : yellow, which === "red" ? value : red);
  };

  return (
    <SettingItem
      id="artThresholds"
      title={t("fields.artThresholds.title")}
      description={t("fields.artThresholds.description")}
      loading={!!(area.saving[YELLOW] || area.saving[RED])}
      saved={Math.max(area.saved[YELLOW] || 0, area.saved[RED] || 0) || undefined}
      wide
    >
      <div>
        <div className={classes.inline}>
          <TextField
            type="number"
            size="small"
            variant="outlined"
            label={t("fields.artThresholds.greenUpTo")}
            value={yellow}
            onChange={change(setYellow, "yellow")}
            onBlur={() => commit.flush()}
            inputProps={{ min: 1, max: 1440 }}
            error={!!error}
            className={classes.numberField}
          />
          <TextField
            type="number"
            size="small"
            variant="outlined"
            label={t("fields.artThresholds.yellowUpTo")}
            value={red}
            onChange={change(setRed, "red")}
            onBlur={() => commit.flush()}
            inputProps={{ min: 1, max: 1440 }}
            error={!!error}
            className={classes.numberField}
          />
        </div>
        {error && (
          <p className={classes.error} role="alert">
            {t(`fields.artThresholds.errors.${error}`)}
          </p>
        )}
      </div>
    </SettingItem>
  );
};

// Mesma lista do backend (DEFAULT_SHORT_REPLY_WORDS em shortReply.ts).
export const DEFAULT_SHORT_REPLY_WORDS = [
  "ok",
  "okay",
  "blz",
  "beleza",
  "certo",
  "combinado",
  "show",
  "top",
  "perfeito",
  "tá",
  "tá bom",
  "obrigado",
  "obrigada",
  "obg",
  "valeu",
  "vlw",
  "kkk",
  "rs",
  "haha",
];
const MAX_SHORT_REPLY_WORDS = 100;
const MAX_SHORT_REPLY_WORD_LENGTH = 30;

/** Erro da lista de palavras (chave de i18n) ou null se ela pode ser gravada. */
export const validateShortReplyWords = (text) => {
  const lines = String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length > MAX_SHORT_REPLY_WORDS) return "tooMany";
  if (lines.some((line) => line.length > MAX_SHORT_REPLY_WORD_LENGTH)) return "tooLong";
  return null;
};

const useStyles = makeStyles({
  restore: { marginTop: 10 },
});

const ShortReplies = ({ area }) => {
  const classes = useSectionStyles();
  const own = useStyles();
  const enabled = area.get("shortReplyFilter", "enabled") === "enabled";
  const defaults = DEFAULT_SHORT_REPLY_WORDS.join("\n");
  const words = useDraftSetting(area, "shortReplyWords", {
    fallback: defaults,
    valid: (text) => !validateShortReplyWords(text),
  });
  const error = validateShortReplyWords(words.value);

  return (
    <SettingsSection title={t("groups.shortReplies.title")} description={t("groups.shortReplies.description")}>
      <SettingItem
        toggle
        {...bindSetting(area, "shortReplyFilter", "enabled")}
        title={t("fields.shortReplyFilter.title")}
        description={t("fields.shortReplyFilter.description")}
      />
      <Collapse in={enabled} unmountOnExit>
        <SettingItem
          id="shortReplyWords"
          stacked
          divider
          title={t("fields.shortReplyWords.title")}
          description={t("fields.shortReplyWords.description")}
          loading={words.saving}
          saved={words.saved}
        >
          <TextField
            multiline
            // Cresce com a lista (a padrão aparece inteira); rolagem só depois de 24 linhas.
            minRows={8}
            maxRows={24}
            fullWidth
            variant="outlined"
            value={words.value}
            onChange={words.onChange}
            onBlur={words.flush}
            error={!!error}
            className={classes.field}
            inputProps={{ "aria-labelledby": "setting-shortReplyWords-title" }}
          />
          {error && (
            <p className={classes.error} role="alert">
              {t(`fields.shortReplyWords.errors.${error}`)}
            </p>
          )}
          <div className={own.restore}>
            <button
              type="button"
              className={classes.secondaryButton}
              onClick={() => words.change(defaults)}
              disabled={words.value === defaults}
            >
              {t("fields.shortReplyWords.restore")}
            </button>
          </div>
        </SettingItem>
        <SettingItem
          toggle
          {...bindSetting(area, "shortReplyStickers", "enabled")}
          title={t("fields.shortReplyStickers.title")}
          description={t("fields.shortReplyStickers.description")}
        />
        <SettingItem
          toggle
          {...bindSetting(area, "shortReplyAutoClose", "disabled")}
          title={t("fields.shortReplyAutoClose.title")}
          description={t("fields.shortReplyAutoClose.description")}
        />
      </Collapse>
    </SettingsSection>
  );
};

const Indicators = ({ area }) => (
  <>
    <SettingsSection title={t("groups.noResponse.title")} description={t("groups.noResponse.description")}>
      <SettingItem
        toggle
        {...bindSetting(area, "noResponseIndicator", "disabled")}
        title={t("fields.noResponseIndicator.title")}
        description={t("fields.noResponseIndicator.description")}
      />
      <SettingItem
        toggle
        {...bindSetting(area, "unrepliedClockIcon", "disabled")}
        title={t("fields.unrepliedClockIcon.title")}
        description={t("fields.unrepliedClockIcon.description")}
      />
      <SettingItem
        toggle
        {...bindSetting(area, "noResponseBusinessHours", "disabled")}
        title={t("fields.noResponseBusinessHours.title")}
        description={t("fields.noResponseBusinessHours.description")}
      />
    </SettingsSection>

    <SettingsSection title={t("groups.responseTime.title")} description={t("groups.responseTime.description")}>
      <ArtThresholds area={area} />
    </SettingsSection>

    <ShortReplies area={area} />
  </>
);

export default Indicators;
